import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import Datastore from "../../communication/Datastore";

import store, { IAppState } from "../../state/Store";
import { initialiseClientSystems } from "../../state/core/ClientSystem";

import UserActivity from "../../types/admin/UserActivity";
import UserActivityLogin from "../../types/admin/UserActivityLogin";
import ClientSystem from "../../types/core/ClientSystem";
import UserDigest from "../../types/core/UserDigest";
import { getDigestDetails } from "../core/User";
import { StartMonitor, StopMonitor } from "../../sockets/statusManager";

// Define the type for our state store entry
interface ActivityState {
    activity: UserActivity[];
    activityLogins: UserActivityLogin;
    activityStatus: string;
    loginsStatus: string;
    activityAge?: Date;
    loginsAge?: Date;
    error: string;
}

// Define the initial state for our state store entry
const initialState: ActivityState = {
    activity: [],
    activityLogins: null,
    activityStatus: "idle",
    loginsStatus: "idle",
    error: null,
};

interface ActivityWatchPayload {
    activity: UserActivity[];
    activityLogins: UserActivityLogin;
}

// Create a slice to hold all our reducers
const activitySlice = createSlice({
    name: "activity",
    initialState: initialState,
    reducers: {
        setActivity: (state, action: PayloadAction<UserActivity[]>) => {
            state.activity = action.payload;
        },
        setActivityLogins: (
            state,
            action: PayloadAction<UserActivityLogin>,
        ) => {
            state.activityLogins = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    activity: rawData?.payload?.activity?.activity,
                    activityLogins: rawData?.payload?.activity?.activityLogins,
                    activityStatus:
                        !rawData?.payload?.activity?.activity ||
                        !rawData?.payload?.activity?.activity.length
                            ? "idle"
                            : "succeeded",
                    loginsStatus: !rawData?.payload?.activity?.activityLogins
                        ? "idle"
                        : "succeeded",
                    activityAge:
                        rawData?.payload?.activity?.activityAge?.toDate(),
                    loginsAge: rawData?.payload?.activity?.loginsAge?.toDate(),
                    error: null,
                };
            })
            .addCase(fetchActivity.pending, (state, action) => {
                state.activityStatus = "loading";
            })
            .addCase(fetchActivity.fulfilled, (state, action) => {
                state.activityStatus = "succeeded";
                state.activityAge = new Date();
                state.activity = processActivityData(
                    action.payload.activity,
                    action.payload.clientSystems,
                    action.payload.users,
                );
            })
            .addCase(fetchActivity.rejected, (state, action) => {
                state.activityStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchLogins.pending, (state, action) => {
                state.loginsStatus = "loading";
            })
            .addCase(fetchLogins.fulfilled, (state, action) => {
                state.loginsStatus = "succeeded";
                state.loginsAge = new Date();
                state.activityLogins = action.payload;
            })
            .addCase(fetchLogins.rejected, (state, action) => {
                state.loginsStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const processActivityData = (
    activity: UserActivity[],
    clientSystems: ClientSystem[],
    users: UserDigest[],
) => {
    return activity.reduce((prev: UserActivity[], current: UserActivity) => {
        let existing = prev.find(item => item.userID === current.userID);

        if (existing) {
            current.systems.forEach(system => {
                if (
                    !existing.systems.find(
                        item => item.systemID === system.systemID,
                    )
                ) {
                    system.clientSystem = clientSystems.find(
                        item => item.id === system.systemID,
                    );
                    existing.systems.push(system);
                }
            });
        } else {
            current.user = users.find(item => item.id === current.userID);
            current.systems.forEach(
                system =>
                    (system.clientSystem = clientSystems.find(
                        item => item.id === system.systemID,
                    )),
            );
            prev.push(new UserActivity(current));
        }

        return prev;
    }, [] as UserActivity[]);
};

// This is a thunk, they are used to load the state
const fetchActivity = createAsyncThunk(
    "activity/fetchActivity",
    async (_, thunkAPI) => {
        const response = await Promise.all([
            initialiseClientSystems(),
            Datastore.getType<UserActivity[]>("api/Activity/Summary"),
        ]);
        const users = await thunkAPI.dispatch(
            getDigestDetails(response[1].map(item => item.userID)),
        );
        const { clientSystem } = store.getState().db;
        
        return {
            clientSystems: clientSystem.clientSystems,
            users: users.payload as UserDigest[],
            activity: response[1],
        };
    },
    {
        condition: (_, { getState }) => {
            const { activity } = (getState() as IAppState).db;
            return (
                activity.activityStatus !== "loading" &&
                (!activity.activityAge ||
                    activity.activityAge.addSeconds(30) < new Date())
            );
        },
    },
);
const fetchLogins = createAsyncThunk(
    "activity/fetchLogins",
    async () => {
        return await Datastore.getType<UserActivityLogin>(
            "api/Activity/Logins",
        );
    },
    {
        condition: (_, { getState }) => {
            const { activity } = (getState() as IAppState).db;
            return (
                activity.loginsStatus !== "loading" &&
                (!activity.loginsAge ||
                    activity.loginsAge.addMinutes(2) < new Date())
            );
        },
    },
);
export const initialiseActivity = createAsyncThunk(
    "activity/initialiseActivity",
    async (_, thunkAPI) => {
        thunkAPI.dispatch(fetchActivity());
        thunkAPI.dispatch(fetchLogins());
    },
);

const { setActivity, setActivityLogins } = activitySlice.actions;

const watchType: string = "activitystatus";
let watching: boolean = false;

export function WatchActivity(schoolId: number) {
    if (!watching) {
        watching = true;
        StartMonitor(
            watchType,
            schoolId,
            10000,
            false,
            (data: ActivityWatchPayload) => {
                const { user, clientSystem } = (store.getState() as IAppState).db;
                store.dispatch(setActivity(processActivityData(
                    data.activity,
                    clientSystem.clientSystems,
                    user.digest)));
                store.dispatch(setActivityLogins(data.activityLogins));
            },
            () => watching = false);
    }
}

export function StopWatchActivity(schoolId: number) {
    if (watching) {
        watching = false;
        StopMonitor(
            watchType,
            schoolId);
    }
}

export default activitySlice.reducer;
